import { useEffect, useMemo } from "react";

import { IntlProvider } from "react-intl";
import { useSearchParams } from "react-router-dom";

import { trackingAdapter } from "../lib/TrackingAdapter";
import de from "../locales/lang/de.json";
import es from "../locales/lang/es.json";
import fr from "../locales/lang/fr.json";
import it from "../locales/lang/it.json";
import ja from "../locales/lang/ja.json";
import ko from "../locales/lang/ko.json";
import pt_BR from "../locales/lang/pt_BR.json";
import ru from "../locales/lang/ru.json";
import zh from "../locales/lang/zh-Hans.json";
import zh_Hant from "../locales/lang/zh-Hant.json";
import type { SupportedLocale } from "../types";

const messages: { [locale in SupportedLocale]: Record<string, string> } = {
  en: {},
  de,
  es,
  fr,
  ja,
  it,
  ko,
  "pt-BR": pt_BR,
  ru,
  zh,
  "zh-hant": zh_Hant,
  "zh-hans": zh,
} as const;

const supportedLocales = Object.keys(messages);

function getLocaleFromParam(langParam: string | null): SupportedLocale | null {
  if (!langParam) return null;

  const normalizedLang = langParam.toLowerCase();

  if (supportedLocales.includes(normalizedLang)) {
    return normalizedLang as SupportedLocale;
  }

  if (normalizedLang.startsWith("zh")) {
    if (
      normalizedLang.includes("hant") ||
      normalizedLang === "zh-tw" ||
      normalizedLang === "zh-hk"
    ) {
      return "zh-hant";
    }
    return "zh-hans";
  }

  if (normalizedLang.startsWith("pt")) {
    return "pt-BR";
  }

  const baseLang = normalizedLang.split("-")[0];
  if (supportedLocales.includes(baseLang)) {
    return baseLang as SupportedLocale;
  }

  return null;
}

function getBrowserLocale(): SupportedLocale {
  const browserLanguages = navigator.languages ?? [navigator.language];

  for (const lang of browserLanguages) {
    const locale = getLocaleFromParam(lang);
    if (locale) return locale;
  }

  return "en";
}

interface BrowserLocaleIntlProviderProps {
  children: React.ReactNode;
}

export function BrowserLocaleIntlProvider({ children }: BrowserLocaleIntlProviderProps) {
  const [searchParams] = useSearchParams();
  const langParam = searchParams.get("lang");

  const locale = useMemo(() => {
    const paramLocale = getLocaleFromParam(langParam);
    if (paramLocale) return paramLocale;

    return getBrowserLocale();
  }, [langParam]);

  useEffect(() => {
    trackingAdapter.setLocale(locale);
    return () => trackingAdapter.setLocale(null);
  }, [locale]);

  return (
    <IntlProvider locale={locale} messages={messages[locale]}>
      {children}
    </IntlProvider>
  );
}
